// Analytics wire contract — the content-free aggregate shapes the maintainer
// `/analytics` dashboard renders, shared between apps/server (which computes
// them out of Postgres in `apps/server/src/analytics/`) and apps/web (which
// renders them, Phase 6).
//
// **Strictly content-free by construction.** Every field here is an id, a
// count, an enum, money, or a timestamp. There is deliberately NO title,
// project path, branch name, MR URL, or transcript snippet: an attributed row
// carrying any of those would reconstruct *what* a customer's user worked on.
// When adding a field, ask "could this leak content?" before it ships.
//
// **Cost is metered across every surface (analytics+postgres Phase 9).**
// `totalCostUsd` is the sum over all model spend; `costBySurface` splits it into
// `coding` / `pm` / `handoff` / `workflow`. `codingCostUsd` survives as the
// coding component (back-compat) — its old "never total" caveat is gone now that
// the breakdown is complete. The remaining caveat is that USD is **derived from
// token counts** (Anthropic reports tokens, not dollars) via `@loom/shared/pricing`
// and is best-effort / dashboard-grade, NOT an invoice.
//
// **Small-cell suppression** is applied at the data boundary
// (`apps/server/src/analytics/query.ts`), not here: a cohort count below the
// `k`-threshold comes across as `{ value: null, suppressed: true }`. Exact
// per-customer numbers are fine to surface, so this is a presentation default,
// not a necessity — but it is consistent across every route because it lives
// under the query layer.

import { z } from "zod";

import { SPEND_COST_SOURCES } from "../spend/index.js";

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/**
 * Money as a fixed-precision decimal **string** (6 dp), never a float. Mirrors
 * the `numeric(12,6)` columns; serialising through a JS number would reintroduce
 * the drift the schema exists to avoid.
 */
export const UsdSchema = z
  .string()
  .regex(/^-?\d+\.\d{6}$/, "USD must be a 6-dp decimal string");
export type Usd = z.infer<typeof UsdSchema>;

/**
 * Spend split across the four metered surfaces (analytics+postgres Phase 9).
 * Each component is a 6-dp USD string; `coding + pm + handoff + workflow`
 * reconciles to the row's `totalCostUsd`. All four surfaces are now metered, so
 * the split is exhaustive (no "unmetered remainder").
 */
export const CostBySurfaceSchema = z
  .object({
    coding: UsdSchema,
    pm: UsdSchema,
    handoff: UsdSchema,
    workflow: UsdSchema,
  })
  .strict();
export type CostBySurface = z.infer<typeof CostBySurfaceSchema>;

/**
 * A cohort count that may be withheld by small-cell suppression. `value` is the
 * exact count, or `null` when the underlying cohort fell below the threshold;
 * `suppressed` is the explicit flag so the UI can render a "<k" band rather than
 * mistaking a suppressed cell for a zero.
 */
export const SuppressibleCountSchema = z
  .object({
    value: z.number().int().nonnegative().nullable(),
    suppressed: z.boolean(),
  })
  .strict()
  .refine((c) => (c.suppressed ? c.value === null : c.value !== null), {
    message: "suppressed counts must have a null value, and vice versa",
  });
export type SuppressibleCount = z.infer<typeof SuppressibleCountSchema>;

/**
 * Content-free reference to a GitLab user. The numeric id is the attribution
 * handle; the display name is resolved against GitLab on read (Phase 5) and
 * never persisted or shipped on aggregate rows.
 */
export const UserRefSchema = z
  .object({
    gitlabHost: z.string(),
    gitlabUserId: z.number().int().nonnegative(),
  })
  .strict();
export type UserRef = z.infer<typeof UserRefSchema>;

/**
 * Reference to a GitLab project — id + host plus the namespace path so the UI
 * can render the human name instead of the bare id. `projectPath` is the GitLab
 * namespace path (e.g. `team/alpha`); it may be empty for rows whose dimension
 * predates path capture, in which case the UI falls back to the id.
 *
 * `gitlabProjectId` is negative for a GitHub repository (`@loom/shared/forge`),
 * so the id is any integer: one GitHub row must not fail the parse of a whole
 * overview. Its host label and pipeline link are still GitLab's (a follow-up).
 */
export const ProjectRefSchema = z
  .object({
    gitlabHost: z.string(),
    gitlabProjectId: z.number().int(),
    projectPath: z.string(),
  })
  .strict();
export type ProjectRef = z.infer<typeof ProjectRefSchema>;

/** Optional half-open activity window echoed back on a response. */
export const AnalyticsRangeSchema = z
  .object({
    from: z.string().datetime().nullable(),
    to: z.string().datetime().nullable(),
  })
  .strict();
export type AnalyticsRange = z.infer<typeof AnalyticsRangeSchema>;

// ---------------------------------------------------------------------------
// Aggregate shapes
// ---------------------------------------------------------------------------

/**
 * Top-level totals across the whole (gated) population. These are large
 * aggregates, never suppressed — suppression only bites the per-cohort
 * breakdowns below.
 */
export const AnalyticsOverviewSchema = z
  .object({
    range: AnalyticsRangeSchema,
    sessionCount: z.number().int().nonnegative(),
    userCount: z.number().int().nonnegative(),
    projectCount: z.number().int().nonnegative(),
    sessionsUsingPm: z.number().int().nonnegative(),
    sessionsUsingCoding: z.number().int().nonnegative(),
    /** Coding component of total spend (back-compat alias of
     *  `costBySurface.coding`). */
    codingCostUsd: UsdSchema,
    /** Total model spend across every surface. */
    totalCostUsd: UsdSchema,
    /** Per-surface breakdown of `totalCostUsd`. */
    costBySurface: CostBySurfaceSchema,
  })
  .strict();
export type AnalyticsOverview = z.infer<typeof AnalyticsOverviewSchema>;

/**
 * One model's aggregate spend over the queried window — the per-model
 * breakdown of `totalCostUsd` (one entry per `fact_model_spend.model_id`).
 * `modelId` is a catalogue identifier (`claude-fable-5`, …), content-free.
 * `costUsd` is the canonical figure; `reportedCostUsd` is the sum of the
 * source-reported USD over the rows that had one, or `null` when none did.
 * The two DIFFER on a repriced row — the `claude` CLI converts its tokens on a
 * $3/$15 sonnet catalog while Anthropic bills Sonnet 5 and 5.5 at $2/$10 — so
 * provenance rides its own `costSource` field rather than being inferred from
 * "a reported figure exists". Over a group it is the strongest claim present
 * (`repriced` > `reported` > `priced`), matching the pre-existing rule that a
 * single reported row labelled the whole model row.
 */
export const AnalyticsModelRowSchema = z
  .object({
    modelId: z.string(),
    costUsd: UsdSchema,
    reportedCostUsd: UsdSchema.nullable(),
    costSource: z.enum(SPEND_COST_SOURCES),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    cacheCreationTokens: z.number().int().nonnegative(),
    cacheReadTokens: z.number().int().nonnegative(),
    callCount: z.number().int().nonnegative(),
  })
  .strict();
export type AnalyticsModelRow = z.infer<typeof AnalyticsModelRowSchema>;

/** One project's aggregate usage. Counts are suppressible; cost is not. */
export const AnalyticsProjectRowSchema = z
  .object({
    project: ProjectRefSchema,
    sessionCount: SuppressibleCountSchema,
    userCount: SuppressibleCountSchema,
    codingCostUsd: UsdSchema,
    totalCostUsd: UsdSchema,
    costBySurface: CostBySurfaceSchema,
  })
  .strict();
export type AnalyticsProjectRow = z.infer<typeof AnalyticsProjectRowSchema>;

/**
 * One named user's aggregate usage — the attributed drill-down gated behind the
 * maintainer tier (Phase 5).
 */
export const AnalyticsUserRowSchema = z
  .object({
    user: UserRefSchema,
    sessionCount: SuppressibleCountSchema,
    codingCostUsd: UsdSchema,
    totalCostUsd: UsdSchema,
    costBySurface: CostBySurfaceSchema,
    lastActiveAt: z.string().datetime(),
  })
  .strict();
export type AnalyticsUserRow = z.infer<typeof AnalyticsUserRowSchema>;

/**
 * A named drill-down row with the GitLab identity **resolved live on read**
 * (analytics+postgres Phase 6). The stored facts carry only the numeric id
 * (`AnalyticsUserRow`); the access layer (`apps/server/src/http/analytics.ts`)
 * resolves the display name + username against GitLab on the viewer's own token
 * and never persists them. Resolution is best-effort: a lookup miss leaves both
 * `null` and the numeric `user.gitlabUserId` still identifies the row. This
 * shape rides ONLY the gated, audited `?scope=users` view — never the
 * content-free aggregate rows.
 */
export const AnalyticsNamedUserRowSchema = AnalyticsUserRowSchema.extend({
  displayName: z.string().nullable(),
  username: z.string().nullable(),
}).strict();
export type AnalyticsNamedUserRow = z.infer<typeof AnalyticsNamedUserRowSchema>;

/**
 * One session's row on the named drill-down (the session action ledger). Rides
 * ONLY the gated, audited `?scope=sessions` / `?scope=session` views — a
 * session row is individual data (it names who ran it). Content-free beyond
 * that: the opaque session id, flags, counts and money — never a title or
 * branch name (those stay in `sessions_index` / the encrypted stores).
 */
export const AnalyticsSessionRowSchema = z
  .object({
    sessionId: z.string(),
    user: UserRefSchema,
    project: ProjectRefSchema,
    createdAt: z.string().datetime(),
    lastActiveAt: z.string().datetime(),
    status: z.string(),
    usedPm: z.boolean(),
    usedCoding: z.boolean(),
    hasOpenMr: z.boolean(),
    pipelineCount: z.number().int().nonnegative(),
    /** GitLab id of the session's most-recently-triggered sandbox pipeline, or
     *  `null` when it never triggered one. The client rebuilds the pipeline
     *  deep-link from this + the project natural key (kept content-free — an
     *  id, not a URL). `.nullish()` so a pre-migration server omitting the key
     *  still parses during a rolling deploy (consumers treat absent as null). */
    lastPipelineId: z.number().int().positive().nullish(),
    /** Total model spend attributed to this session (all surfaces). */
    totalCostUsd: UsdSchema,
    /** Captured action-ledger rows for this session. */
    actionCount: z.number().int().nonnegative(),
    /**
     * What the conversation WAS — the closed `@loom/shared/session` enum
     * (`chat` | `issue` | `implement` | `unknown`), so a row reads as
     * "Refinement #84" rather than `a3f91c2e…`. A `.nullish()` STRING, not the
     * enum: absent means the server predates the column and `null` means the
     * projection row does, and the UI shows neither as a kind. Pinning the enum
     * here would make a future member a parse failure in every old bundle.
     */
    kind: z.string().nullish(),
    /** The issue this conversation was last bound to — a bare number, the same
     *  content class the action rows already carry. `null` on a plain chat. */
    issueIid: z.number().int().nonnegative().nullish(),
    /**
     * Does a readable chat transcript still exist for this session, and may
     * THIS viewer open it?
     *
     * Two facts folded into one flag on purpose, because the UI does the same
     * thing with both: it renders no link. A session that ran before the
     * replay log existed has no row; so does one whose 30-day TTL has passed.
     * A link that leads to "nothing here" is worse than no link, and telling a
     * non-transcript viewer WHY there is no link would leak that the surface
     * exists. Absent / `false` → no link. Only ever `true` for a viewer on
     * `LOOM_TRANSCRIPT_VIEWERS`.
     */
    hasTranscript: z.boolean().nullish(),
  })
  .strict();
export type AnalyticsSessionRow = z.infer<typeof AnalyticsSessionRowSchema>;

/**
 * A session row with the GitLab identity resolved live on read — the same
 * best-effort, never-persisted resolution `AnalyticsNamedUserRow` gets, applied
 * to the session views so "who ran this" reads as a person rather than as
 * `User 450`. A lookup miss leaves both `null` and the numeric id still
 * identifies the row.
 */
export const AnalyticsNamedSessionRowSchema = AnalyticsSessionRowSchema.extend({
  // `.nullish()`, unlike the user row's `.nullable()`, and the difference is a
  // rolling deploy. The user row shipped these keys with its endpoint; the
  // session views gained them later, so a server revision that predates them
  // omits them — and this envelope is `.strict()`, which would make a new
  // bundle's parse THROW against an old revision and take the whole panel down.
  // Absent reads as "unresolved", which is the same thing `null` means here.
  displayName: z.string().nullish(),
  username: z.string().nullish(),
}).strict();
export type AnalyticsNamedSessionRow = z.infer<
  typeof AnalyticsNamedSessionRowSchema
>;

/**
 * One captured action on a session's timeline (`?scope=session`). `kind` is the
 * closed `@loom/shared/actions` taxonomy; `name` is a catalogue identifier
 * (tool name / workflow id / sandbox phase / error code) — never user or model
 * text.
 */
export const AnalyticsSessionActionRowSchema = z
  .object({
    kind: z.string(),
    name: z.string(),
    outcome: z.string().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    issueIid: z.number().int().nonnegative().nullable(),
    occurredAt: z.string().datetime(),
  })
  .strict();
export type AnalyticsSessionActionRow = z.infer<
  typeof AnalyticsSessionActionRowSchema
>;

/** A session row + its full action timeline — the `?scope=session` payload. */
export const AnalyticsSessionDetailSchema = z
  .object({
    session: AnalyticsSessionRowSchema,
    actions: z.array(AnalyticsSessionActionRowSchema),
  })
  .strict();
export type AnalyticsSessionDetail = z.infer<
  typeof AnalyticsSessionDetailSchema
>;

/** One day on the usage timeseries (aggregated across users). */
export const AnalyticsDailyPointSchema = z
  .object({
    /** Calendar day, `YYYY-MM-DD`. */
    day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    sessionCount: SuppressibleCountSchema,
    codingCostUsd: UsdSchema,
    totalCostUsd: UsdSchema,
    costBySurface: CostBySurfaceSchema,
  })
  .strict();
export type AnalyticsDailyPoint = z.infer<typeof AnalyticsDailyPointSchema>;

// ---------------------------------------------------------------------------
// Chat transcript — the one surface here that carries CONTENT
// ---------------------------------------------------------------------------

/**
 * THE EXCEPTION, stated where it is easiest to find.
 *
 * Everything above this line is content-free by contract: ids,
 * counts, money, timestamps, and catalogue identifiers. The shapes below are
 * the deliberate opposite — the words a requester typed and the words the agent
 * answered with, read out of `app.session_events` (the encrypted chat-replay
 * log) and projected for an operator.
 *
 * That is why they do NOT ride the analytics gate. They ride a third allowlist
 * (`LOOM_TRANSCRIPT_VIEWERS`, itself a subset of the org-wide viewers), they
 * are audited under their own `transcript` scope, and a viewer who is not on
 * that list is never told the surface exists. Nothing here is ever folded into
 * an aggregate, a rollup or the star schema.
 *
 * What the projection does NOT carry, and why:
 *   - tool ARGUMENTS and tool RESULTS. A `fetch_figma_design` result is a
 *     24 KB design digest; a `propose_card` argument is the whole card. The
 *     entry keeps the tool's NAME and whether it worked — the same two facts
 *     the action ledger already keeps — and drops the payload.
 *   - the tail era. Post-green narration walks full repo paths and the user
 *     never saw it; no client surface renders it as prose, and this is a
 *     client surface.
 *   - raw stdout/stderr, lifecycle churn, readiness cards and Figma sketches.
 *     Those are the session's machinery, not its conversation.
 */
export const SessionTranscriptEntrySchema = z
  .object({
    /**
     * `user` — a turn the person typed. `assistant` — prose the agent replied
     * with, from either lane (the in-process servant's `assistant_delta` or the
     * sandbox's `bridge_stream` claude JSONL). `tool` — a call, by name and
     * outcome only. `error` — an error frame the session surfaced.
     */
    kind: z.enum(["user", "assistant", "tool", "error"]),
    /** Prose, on `user` / `assistant` / `error`. Empty string on `tool`. */
    text: z.string(),
    /** Tool name on `tool`, error code on `error`; `null` otherwise. */
    name: z.string().nullable(),
    /** `ok` / `error` on a completed `tool`; `null` while it never returned. */
    outcome: z.string().nullable(),
    /** The issue this turn was bound to, on a `user` turn that carried one. */
    issueIid: z.number().int().nonnegative().nullable(),
    /** Event sequence number — the transcript's own stable ordering. */
    seq: z.number().int().nonnegative(),
    at: z.string().datetime(),
  })
  .strict();
export type SessionTranscriptEntry = z.infer<
  typeof SessionTranscriptEntrySchema
>;

/** One session's projected conversation — the `?scope=transcript` payload. */
export const SessionTranscriptSchema = z
  .object({
    sessionId: z.string(),
    entries: z.array(SessionTranscriptEntrySchema),
    /**
     * How many stored events the projection read, against how many entries it
     * produced. The gap is the machinery it dropped, and printing both is how
     * the screen avoids reading as "this is everything that happened".
     */
    eventCount: z.number().int().nonnegative(),
    /** When the log's oldest and newest kept events landed; `null` on empty. */
    firstAt: z.string().datetime().nullable(),
    lastAt: z.string().datetime().nullable(),
    /**
     * The replay log is FIFO-capped at 4000 events, so a long session's early
     * turns are already gone from its only durable copy. `true` when the stored
     * array is at the cap — the transcript begins mid-conversation and says so.
     */
    capped: z.boolean(),
  })
  .strict();
export type SessionTranscript = z.infer<typeof SessionTranscriptSchema>;
